import { useState, useRef } from 'react';
import type { DragEvent } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, X, Star, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { validateImageFile, ACCEPTED_IMAGE_EXTENSIONS } from '@/lib/image-validation';

export interface AssetPhoto {
  id: string;
  url: string;
  isPrimary: boolean;
  uploadedDate: string;
  fileName?: string;
  fileSize?: number;
}

interface AssetPhotosTabProps {
  readonly photos: AssetPhoto[];
  readonly onChange: (photos: AssetPhoto[]) => void;
  readonly disabled?: boolean;
  readonly maxPhotos?: number;
}

export function AssetPhotosTab({ 
  photos, 
  onChange, 
  disabled = false,
  maxPhotos = 5 
}: AssetPhotosTabProps): React.ReactElement {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const remainingSlots = maxPhotos - photos.length;
  const canUploadMore = photos.length < maxPhotos;

  const validateFile = (file: File): string | null => {
    return validateImageFile(file);
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setError(null);

    // Check if adding these files would exceed the limit
    if (photos.length + files.length > maxPhotos) {
      setError(`Cannot upload ${files.length} photo(s). You can only upload ${remainingSlots} more photo(s).`);
      return;
    }

    const newPhotos: AssetPhoto[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        continue;
      }

      try {
        // Convert file to data URL
        const dataUrl = await readFileAsDataURL(file);
        
        const newPhoto: AssetPhoto = {
          id: `photo-${Date.now()}-${i}`,
          url: dataUrl,
          isPrimary: photos.length === 0 && newPhotos.length === 0, // First photo is primary
          uploadedDate: new Date().toISOString(),
          fileName: file.name,
          fileSize: file.size,
        };

        newPhotos.push(newPhoto);
      } catch (err) {
        console.error('Error reading file:', err);
        setError('Failed to read file. Please try again.');
      }
    }

    if (newPhotos.length > 0) {
      onChange([...photos, ...newPhotos]);
    }
  };

  const readFileAsDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && canUploadMore) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || !canUploadMore) return;

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleDeletePhoto = (photoId: string) => {
    const updatedPhotos = photos.filter(p => p.id !== photoId);
    
    // If we deleted the primary photo, make the first remaining photo primary
    const hadPrimary = photos.find(p => p.id === photoId)?.isPrimary;
    if (hadPrimary && updatedPhotos.length > 0) {
      updatedPhotos[0].isPrimary = true;
    }

    onChange(updatedPhotos);
  };

  const handleSetPrimary = (photoId: string) => {
    const updatedPhotos = photos.map(p => ({
      ...p,
      isPrimary: p.id === photoId,
    }));
    onChange(updatedPhotos);
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      <Card className="relative border border-border/40">
        {!disabled && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-primary/90 text-primary-foreground px-3 py-1 text-xs font-medium rounded-full shadow-sm">
            Editing
          </div>
        )}
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Asset Photos</CardTitle>
              <CardDescription>
                Upload up to {maxPhotos} photos. The first photo will be set as primary by default.
              </CardDescription>
            </div>
            <Badge variant="secondary" className="text-sm">
              {photos.length} of {maxPhotos} photos
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Upload Area */}
          {canUploadMore && !disabled && (
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
                isDragging 
                  ? "border-primary bg-primary/5" 
                  : "border-muted-foreground/25 hover:border-muted-foreground/50"
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-full bg-muted p-4">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    Drag and drop photos here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG, GIF, WEBP • Max 5MB per file • {remainingSlots} slot{remainingSlots !== 1 ? 's' : ''} remaining
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Choose Files
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_IMAGE_EXTENSIONS}
                  multiple
                  className="hidden"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileSelect(e.target.files)}
                />
              </div>
            </div>
          )}

          {/* Maximum Photos Reached */}
          {!canUploadMore && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Maximum of {maxPhotos} photos reached. Delete a photo to upload a new one.
              </AlertDescription>
            </Alert>
          )}

          {/* Photos Grid */}
          {photos.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Uploaded Photos</Label>
                <p className="text-sm text-muted-foreground">
                  Click the star to set a photo as primary
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {photos.map((photo) => (
                  <div
                    key={photo.id}
                    className={cn(
                      "relative group rounded-lg border-2 overflow-hidden transition-all",
                      photo.isPrimary 
                        ? "border-primary shadow-md ring-2 ring-primary/20" 
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    {/* Primary Badge */}
                    {photo.isPrimary && (
                      <Badge 
                        className="absolute top-2 left-2 z-10 bg-primary text-primary-foreground shadow-lg"
                      >
                        <Star className="h-3 w-3 mr-1 fill-current" />
                        Primary
                      </Badge>
                    )}

                    {/* Image */}
                    <div className="aspect-square bg-muted relative">
                      <img
                        src={photo.url}
                        alt={photo.fileName ?? 'Asset photo'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EError%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      
                      {/* Overlay Actions */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {!photo.isPrimary && !disabled && (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => handleSetPrimary(photo.id)}
                            className="shadow-lg"
                          >
                            <Star className="h-4 w-4 mr-1" />
                            Set Primary
                          </Button>
                        )}
                        {!disabled && (
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeletePhoto(photo.id)}
                            className="shadow-lg"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Photo Info */}
                    <div className="p-3 bg-card space-y-1">
                      <p className="text-sm font-medium truncate" title={photo.fileName}>
                        {photo.fileName ?? 'Untitled'}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{formatFileSize(photo.fileSize)}</span>
                        <span>{new Date(photo.uploadedDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 space-y-3">
              <div className="flex justify-center">
                <div className="rounded-full bg-muted p-6">
                  <ImageIcon className="h-12 w-12 text-muted-foreground" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">
                  No photos uploaded yet
                </p>
                <p className="text-xs text-muted-foreground">
                  Upload up to {maxPhotos} photos to showcase this asset
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
